import { Link } from "@inertiajs/react"
import { ExternalLink, Mail, Phone, Slack, User } from "lucide-react"

import { ChatInterface } from "@/components/chat-interface"
import { Badge } from "@/components/ui/badge"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"

// Mock employee data
const employeeData = {
  john: {
    name: "John Doe",
    role: "Frontend Engineer",
    email: "john.doe@company.com",
    phone: "+1 (555) 123-4567",
    avatar: "https://randomuser.me/api/portraits/men/28.jpg",
    bio: "Experienced frontend developer with 8+ years in React and TypeScript. Passionate about creating intuitive user experiences and mentoring junior developers.",
    skills: ["React", "TypeScript", "Next.js", "Tailwind CSS", "Node.js"],
    projects: ["Local Setup Guide", "Feature Toggle UI", "Code Review Process"],
  },
  jane: {
    name: "Jane Smith",
    role: "Tech Lead",
    email: "jane.smith@company.com",
    phone: "+1 (555) 123-4568",
    avatar: "https://randomuser.me/api/portraits/women/9.jpg",
    bio: "Technical leader with expertise in system architecture and team management. Focused on building scalable solutions and fostering collaborative development practices.",
    skills: [
      "System Architecture",
      "Team Leadership",
      "React",
      "AWS",
      "DevOps",
    ],
    projects: ["Code Review Process", "Repo Setup", "Platform Collaboration"],
  },
  bob: {
    name: "Bob Johnson",
    role: "Platform Engineer",
    email: "bob.johnson@company.com",
    phone: "+1 (555) 123-4570",
    avatar: "https://randomuser.me/api/portraits/men/48.jpg",
    bio: "Engineer focused on backend performance and internal tools. Known for building robust CI/CD systems and improving developer workflows.",
    skills: ["CI/CD", "Kubernetes", "Terraform", "PostgreSQL", "Node.js"],
    projects: [
      "CI Pipeline Setup",
      "API Permissions Management",
      "Monitoring Integration",
    ],
  },
  alice: {
    name: "Alice Brown",
    role: "DevOps Engineer",
    email: "alice.brown@company.com",
    phone: "+1 (555) 123-4571",
    avatar: "https://randomuser.me/api/portraits/women/27.jpg",
    bio: "DevOps engineer dedicated to infrastructure as code, observability, and incident response. Enjoys automating everything.",
    skills: ["Docker", "AWS", "Grafana", "Prometheus", "Bash"],
    projects: ["Log Access Setup", "Infrastructure Scaling", "CI Pipeline"],
  },
  sarah: {
    name: "Sarah Wilson",
    role: "UX Designer",
    email: "sarah.wilson@company.com",
    phone: "+1 (555) 123-4572",
    avatar: "https://randomuser.me/api/portraits/women/7.jpg",
    bio: "UX designer focused on creating intuitive flows and clean user interfaces. Strong believer in user-centered design.",
    skills: [
      "Figma",
      "User Research",
      "Wireframing",
      "Accessibility",
      "Prototyping",
    ],
    projects: [
      "Design System",
      "Figma Handoff Process",
      "User Testing Sessions",
    ],
  },
  mike: {
    name: "Mike Davis",
    role: "Product Manager",
    email: "mike.davis@company.com",
    phone: "+1 (555) 123-4573",
    avatar: "https://randomuser.me/api/portraits/lego/1.jpg",
    bio: "PM who bridges technical and business goals. Enjoys writing specs, gathering feedback, and shipping fast.",
    skills: [
      "Agile",
      "JIRA",
      "Stakeholder Communication",
      "Roadmapping",
      "Feature Planning",
    ],
    projects: ["Design Workflow", "Prototyping", "Cross-Team Sync"],
  },
  emma: {
    name: "Emma Davis",
    role: "iOS Developer",
    email: "emma.davis@company.com",
    phone: "+1 (555) 123-4574",
    avatar: "https://randomuser.me/api/portraits/women/32.jpg",
    bio: "iOS engineer who loves crafting smooth native experiences. Expert in SwiftUI and mobile accessibility.",
    skills: ["Swift", "SwiftUI", "Xcode", "Firebase", "App Store Deployment"],
    projects: [
      "Mobile Onboarding",
      "Firebase Integration",
      "iOS Release Management",
    ],
  },
  li: {
    name: "Li Wei",
    role: "Android Developer",
    email: "li.wei@company.com",
    phone: "+1 (555) 123-4575",
    avatar: "https://randomuser.me/api/portraits/men/26.jpg",
    bio: "Android engineer passionate about performance and Kotlin-first development. Enjoys refactoring legacy codebases.",
    skills: ["Kotlin", "Jetpack Compose", "Gradle", "Crashlytics", "Retrofit"],
    projects: [
      "Mobile App Setup",
      "Push Notification Integration",
      "Android Build Pipeline",
    ],
  },
  sam: {
    name: "Sam Green",
    role: "QA Analyst",
    email: "sam.green@company.com",
    phone: "+1 (555) 123-4576",
    avatar: "https://randomuser.me/api/portraits/men/10.jpg",
    bio: "QA professional with a sharp eye for detail and edge cases. Advocates for early testing and bug prevention.",
    skills: [
      "Manual Testing",
      "Regression Testing",
      "JIRA",
      "TestRail",
      "Exploratory Testing",
    ],
    projects: [
      "Test Environment Deployment",
      "Bug Bash Coordination",
      "Test Case Writing",
    ],
  },
  nina: {
    name: "Nina Patel",
    role: "Automation Engineer",
    email: "nina.patel@company.com",
    phone: "+1 (555) 123-4577",
    avatar: "https://randomuser.me/api/portraits/women/1.jpg",
    bio: "Engineer specializing in automated UI and API testing. Loves turning flaky tests into stable, repeatable checks.",
    skills: ["Selenium", "Playwright", "Cypress", "Python", "CI Integration"],
    projects: ["E2E Test Suite", "Automated Regression Tests", "Load Testing"],
  },
}

interface EmployeeShowProps {
  employee_id: string
  org_id: string
  team_id: string
}

export default function EmployeePage({
  employee_id,
  org_id,
  team_id,
}: EmployeeShowProps) {
  const employee = employeeData[employee_id as keyof typeof employeeData]

  if (!employee) {
    return <div className="p-6">Employee not found</div>
  }

  return (
    <div className="p-6">
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <div className="flex items-start gap-4">
                <img
                  src={employee.avatar || "/placeholder.svg"}
                  alt={employee.name}
                  width={120}
                  height={120}
                  className="rounded-full"
                />
                <div className="flex-1">
                  <div className="mb-2 flex items-center gap-2">
                    <User className="text-primary size-6" />
                    <CardTitle className="text-2xl">{employee.name}</CardTitle>
                  </div>
                  <CardDescription className="mb-4 text-lg">
                    {employee.role}
                  </CardDescription>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <Mail className="size-4" />
                      <span>{employee.email}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="size-4" />
                      <span>{employee.phone}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Slack className="size-4" />
                      <span>Chat with their AI in Slack</span>
                      <ExternalLink className="size-3" />
                    </div>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div>
                  <h3 className="mb-2 font-semibold">About</h3>
                  <p className="text-muted-foreground">{employee.bio}</p>
                </div>

                <div>
                  <h3 className="mb-2 font-semibold">Skills</h3>
                  <div className="flex flex-wrap gap-2">
                    {employee.skills.map((skill) => (
                      <Badge key={skill} variant="secondary">
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="mb-2 font-semibold">Current Projects</h3>
                  <div className="space-y-2">
                    {employee.projects.map((project) => {
                      // Convert project name to URL-friendly format
                      const projectId = project
                        .toLowerCase()
                        .replace(/\s+/g, "-")
                      return (
                        <Link
                          key={project}
                          href={`/orgs/${org_id}/teams/${team_id}/scenarios/${projectId}`}
                          className="group hover:text-primary flex items-center gap-2 transition-colors"
                        >
                          <div className="bg-primary group-hover:bg-primary/80 h-2 w-2 rounded-full" />
                          <span className="text-sm">{project}</span>
                          <ExternalLink className="size-3 opacity-0 transition-opacity group-hover:opacity-100" />
                        </Link>
                      )
                    })}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div>
          <ChatInterface context={employee.name} />
        </div>
      </div>
    </div>
  )
}
